import { getErrorMessage } from '@juvare/common-web-ui/utils';
import { Alert, Box, Chip, Skeleton, Table, TableBody, TableCell, TableRow, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useAlertConfig, useAlertUiTranslation } from '../../../../../hooks';
import { usePlatformFunction } from '../../../../../hooks/usePlatformFunction';
import { ChannelIcon } from '../../ChannelIcon';
export const AlertSummary = ({ data }) => {
    var _a, _b;
    const { t } = useAlertUiTranslation();
    const statusLikeSent = ['sent', 'sending', 'failed', 'archived'].includes((_a = data === null || data === void 0 ? void 0 : data.status) !== null && _a !== void 0 ? _a : '');
    const recipients = useRecipientsFromAdditionalData(data);
    const upSmScreen = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const TableCellSenderSmScreen = () => {
        return (React.createElement(Box, null,
            React.createElement(Typography, { sx: { pb: 2 }, variant: 'subtitle2' }, statusLikeSent ? t('common.sender') : t('common.updatedBy')),
            statusLikeSent ? data.ownerName : data.updatedBy));
    };
    const TableCellRecipientsSmScreen = () => {
        return (React.createElement(Box, null,
            React.createElement(Typography, { sx: { pb: 2 }, variant: 'subtitle2' }, t('common.recipients')),
            recipients));
    };
    const TableCellChannelsSmScreen = () => {
        var _a;
        return (React.createElement(Box, null,
            React.createElement(Typography, { sx: { pb: 2 }, variant: 'subtitle2' }, t('common.deliveryMethod')),
            React.createElement(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 1 } }, (_a = data.channels) === null || _a === void 0 ? void 0 : _a.map((channel) => (React.createElement(Chip, { key: channel, icon: React.createElement(ChannelIcon, { channel: channel }), label: t(`common.channels.${channel}`), size: 'small' }))))));
    };
    return (React.createElement(Box, null,
        React.createElement(Table, null,
            React.createElement(TableBody, null,
                React.createElement(TableRow, null, upSmScreen ? (React.createElement(React.Fragment, null,
                    React.createElement(TableCell, { sx: { width: 0, pl: 0, whiteSpace: 'nowrap' } },
                        React.createElement(Typography, { variant: 'subtitle2' }, statusLikeSent ? t('common.sender') : t('common.updatedBy'))),
                    React.createElement(TableCell, null, statusLikeSent ? data.ownerName : data.updatedBy))) : (React.createElement(TableCell, { sx: { width: 0, pl: 0, whiteSpace: 'nowrap' } }, TableCellSenderSmScreen()))),
                React.createElement(TableRow, null, upSmScreen ? (React.createElement(React.Fragment, null,
                    React.createElement(TableCell, { sx: { width: 0, pl: 0, whiteSpace: 'nowrap' } },
                        React.createElement(Typography, { variant: 'subtitle2' }, t('common.recipients'))),
                    React.createElement(TableCell, null, recipients))) : (React.createElement(TableCell, { sx: { width: 0, pl: 0, whiteSpace: 'nowrap' } }, TableCellRecipientsSmScreen()))),
                React.createElement(TableRow, null, upSmScreen ? (React.createElement(React.Fragment, null,
                    React.createElement(TableCell, { sx: { width: 0, pl: 0, whiteSpace: 'nowrap' } },
                        React.createElement(Typography, { variant: 'subtitle2' }, t('common.deliveryMethod'))),
                    React.createElement(TableCell, null,
                        React.createElement(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 1 } }, (_b = data.channels) === null || _b === void 0 ? void 0 : _b.map((channel) => (React.createElement(Chip, { key: channel, icon: React.createElement(ChannelIcon, { channel: channel }), label: t(`common.channels.${channel}`), size: 'small' }))))))) : (React.createElement(TableCell, { sx: { width: 0, pl: 0, whiteSpace: 'nowrap' } }, TableCellChannelsSmScreen())))))));
};
function useRecipientsFromAdditionalData(data) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
    const { t } = useAlertUiTranslation();
    const { onGetInternalRecipientsSummary } = useAlertConfig();
    let groupCount = 0;
    let groupAccountsCount = 0;
    let organizationCount = 0;
    let organizationAccountsCount = 0;
    let accountCount = 0;
    let ccCount = 0;
    let externalCount = 0;
    let additionalDataError = undefined;
    const statusLikeSent = ['sent', 'sending', 'failed', 'archived'].includes(data.status);
    let counts;
    let recipients = undefined;
    if (typeof ((_a = data.additionalData) === null || _a === void 0 ? void 0 : _a['recipients']) === 'string') {
        try {
            recipients = JSON.parse((_b = data.additionalData) === null || _b === void 0 ? void 0 : _b['recipients']);
        }
        catch (error) {
            console.error(`Failed to parse additional data, alertId=${data.id}`, error);
            additionalDataError = error;
        }
    }
    let params = undefined;
    if (statusLikeSent) {
        if (recipients && recipients.finalCounts) {
            groupCount = recipients.finalCounts.groupCount;
            groupAccountsCount = recipients.finalCounts.groupAccountsCount;
            organizationCount = recipients.finalCounts.organizationCount;
            organizationAccountsCount = recipients.finalCounts.organizationAccountsCount;
            accountCount = recipients.finalCounts.accountCount;
            ccCount = recipients.finalCounts.ccCount;
            externalCount = recipients.finalCounts.externalCount;
            counts = recipients.finalCounts;
        }
        else {
            additionalDataError = 'Old alert, cannot display info about recipients'; // TODO???
        }
    }
    else {
        ccCount = data.emailCc ? data.emailCc.split(',').length : 0;
        if (typeof ((_c = data.additionalData) === null || _c === void 0 ? void 0 : _c['recipients']) === 'string') {
            try {
                const recipients = JSON.parse((_d = data.additionalData) === null || _d === void 0 ? void 0 : _d['recipients']);
                if (recipients) {
                    params = {
                        recipients: recipients,
                        calculateCounts: true,
                        channels: data.channels,
                    };
                }
            }
            catch (error) {
                console.error(`Failed to parse additional data, alertId=${data.id}`, error);
                additionalDataError = error;
            }
        }
    }
    const calculateRecipientsResult = usePlatformFunction('calculateRecipients', params);
    if (calculateRecipientsResult.isLoading) {
        return React.createElement(Skeleton, { sx: { maxWidth: 200 } });
    }
    else if (calculateRecipientsResult.error) {
        return React.createElement(Alert, { severity: 'error' }, getErrorMessage(calculateRecipientsResult.error));
    }
    else if (additionalDataError) {
        return React.createElement(Alert, { severity: 'error' }, getErrorMessage(additionalDataError));
    }
    if (calculateRecipientsResult.data) {
        groupCount = (_h = (_g = (_f = (_e = calculateRecipientsResult.data) === null || _e === void 0 ? void 0 : _e.result) === null || _f === void 0 ? void 0 : _f.counts) === null || _g === void 0 ? void 0 : _g.groupCount) !== null && _h !== void 0 ? _h : 0;
        groupAccountsCount = (_m = (_l = (_k = (_j = calculateRecipientsResult.data) === null || _j === void 0 ? void 0 : _j.result) === null || _k === void 0 ? void 0 : _k.counts) === null || _l === void 0 ? void 0 : _l.groupAccountsCount) !== null && _m !== void 0 ? _m : 0;
        organizationCount = (_r = (_q = (_p = (_o = calculateRecipientsResult.data) === null || _o === void 0 ? void 0 : _o.result) === null || _p === void 0 ? void 0 : _p.counts) === null || _q === void 0 ? void 0 : _q.organizationCount) !== null && _r !== void 0 ? _r : 0;
        organizationAccountsCount = (_v = (_u = (_t = (_s = calculateRecipientsResult.data) === null || _s === void 0 ? void 0 : _s.result) === null || _t === void 0 ? void 0 : _t.counts) === null || _u === void 0 ? void 0 : _u.organizationAccountsCount) !== null && _v !== void 0 ? _v : 0;
        accountCount = (_z = (_y = (_x = (_w = calculateRecipientsResult.data) === null || _w === void 0 ? void 0 : _w.result) === null || _x === void 0 ? void 0 : _x.counts) === null || _y === void 0 ? void 0 : _y.accountCount) !== null && _z !== void 0 ? _z : 0;
        externalCount = (_3 = (_2 = (_1 = (_0 = calculateRecipientsResult.data) === null || _0 === void 0 ? void 0 : _0.result) === null || _1 === void 0 ? void 0 : _1.counts) === null || _2 === void 0 ? void 0 : _2.externalCount) !== null && _3 !== void 0 ? _3 : 0;
        counts = Object.assign(Object.assign({}, ((_6 = (_5 = (_4 = calculateRecipientsResult.data) === null || _4 === void 0 ? void 0 : _4.result) === null || _5 === void 0 ? void 0 : _5.counts) !== null && _6 !== void 0 ? _6 : {})), { groupCount,
            groupAccountsCount,
            organizationCount,
            organizationAccountsCount,
            accountCount,
            externalCount,
            ccCount });
    }
    const groupText = groupCount > 0 ? t('common.groupsCount', { count: groupCount }) : '';
    const organizationText = organizationCount > 0 ? t('common.organizationsCount', { count: organizationCount }) : '';
    const accountText = accountCount > 0 ? t('common.accountsCount', { count: accountCount }) : '';
    const ccText = ccCount > 0 ? t('common.ccCount', { count: ccCount }) : '';
    const externalText = externalCount > 0 ? t('common.externalRecipientsCount', { count: externalCount }) : '';
    let result;
    if (onGetInternalRecipientsSummary && counts) {
        const summary = onGetInternalRecipientsSummary(counts, recipients === null || recipients === void 0 ? void 0 : recipients.internal);
        result = [summary, ccText, externalText].filter((text) => !!text).join(', ');
    }
    else {
        result = [groupText, organizationText, accountText, ccText, externalText].filter((text) => !!text).join(', ');
    }
    return result || t('common.noRecipientsSelected');
}
